/**
 * Describes a possible option for asset downloads(original, view, proxy, repurpose)
 * @class
 * @extends Netx.Model
 */
import Base from '@netx/core/lib/netx-model';

import Collection from '@netx/core/lib/netx-collection';

import {types as downloadOptionTypes} from '../properties/downloadOptions';

export default class DownloadOption extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	// not sure if this is right, but for now it has to be something other than 'id'
	get idAttribute() {
		return 'some-bogus-id';
	}
	/**
	 * @property {object}  defaults                       - The default attribute values
	 * @property {string}  defaults.type                  - The default type (see share/properties/downloadOptions#types)
	 * @property {string}  defaults.name                  - The defualt name ("thumb","preview", asset view name, or PDF contact sheet name [1x1, 3x3, or 9x9])
	 * @property {number}  defaults.id                    - The default processId / attrFieldSet (preset process Id, or metadata AttrFieldSet Id [if zero for Contact Sheet, then use defaults])
	 * @property {array}   defaults.options               - The default options (an array of LabelValue objects)
	 * @property {boolean} defaults.requiresApproval      - The default requires approval (read-only from server)
	 * @property {boolean} defaults.requiresJustification - The default requires justification (read-only from server)
	 * @property {array}   defaults.attributeIds          - The default array of attribute ids (for PDF contact sheets, this is sent as the list of attributes to display)
	 * @property {number}  defaults.organizeContentsBy    - default option for recursive download zip origanization
	 */
	get defaults() {
		return {
			type: '',
			name: '',
			id: 0,
			family: '',
			options: [],
			requiresApproval: false,
			requiresJustification: false,
			attributeIds: [],
			organizeContentsBy: 0, // default to no organization, this is not a user option currently

			// Client attributes
			// For PDF (contactSheet)
			engine: 'java',
			exportOptions: new Collection([]),
			templates: new Collection([]),
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['engine', 'exportOptions', 'templates'];
	}

	////////////////////////////////////
	// RPC METHODS
	////////////////////////////////////
	/**
	 * @property {object}           methods       - The methods available on this model
	 * @property {function|array}   methods.getPDFTemplateOptions - Request the template options for generating a PDF
	 */
	get methods() {
		return {
			getPDFTemplateOptions: ['getPDFTemplateOptions'],
		};
	}
	////////////////////////////////////
	// !RPC METHODS
	////////////////////////////////////

	get parsers() {
		return {
			getPDFTemplateOptions: function (response) {
				response = response || {};
				// Set client attributes
				this.set('engine', response.engine || 'java');
				this.get('exportOptions').reset(response.exportOptions);
				this.get('templates').reset(response.templates);
				return response;
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts) {
		super(attrs, opts);

		// Defer so that any code which created this model can have a chance to do anything right after initialization is finished
		// and just before we shove these client attributes in
		_.defer(() => {
			switch (this.get('type')) {
				case downloadOptionTypes.contactSheet:
					if (!this.has('exportOptions') || !this.has('templates')) {
						this.set({
							exportOptions: new Collection([]),
							templates: new Collection([]),
						});
					}
					this.getPDFTemplateOptions();
					break;
			}
		});
	}

	// Utility for handling that options array
	getOptions() {
		return _.lvbsToObject(this.get('options'));
	}

	setOptions(obj, opts) {
		return this.set({options: _.objectToLvbs(obj)}, opts);
	}

	addOptions(addObject, opts) {
		return this.setOptions(_.extend(this.getOptions(), addObject), opts);
	}

	//TODO: this *Options interface is nonstandard and weird.  Better to have this.options as a sub-model, and use this.self() & this.parse() to pack it/unpack it.
	//see the asset model's .metadata model for an example.

	/** @type {string} */
	static get __name__() {
		return 'DownloadOption';
	}
}
